import { useEffect, useState } from "react";
import API, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ThumbsUp, ThumbsDown, Minus, X } from "lucide-react";

/**
 * Reflection loop UI surface (P1.1).
 *
 * On every app load (when the user is authenticated), checks for the most
 * recent unreflected export. If one exists, surfaces a non-blocking modal
 * asking whether the playbook actually worked. Outcomes propagate to:
 *   - the playbook's success/failure counts
 *   - all `source_library_id`-attributed library blocks' success/failure counts
 *
 * Lives at the App-level so it can intercept the user immediately, without
 * waiting for them to navigate to a specific page.
 */
export function ReflectionPrompt() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [pendingOutcome, setPendingOutcome] = useState(null);

  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/reflections/pending");
        if (cancelled) return;
        if (data?.pending) {
          setPending(data.pending);
          setOpen(true);
        }
      } catch {
        // Silent — never block app load on this.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const submit = async (outcome, withNotes = "") => {
    if (!pending) return;
    setSubmitting(true);
    try {
      await API.post("/reflections", {
        export_id: pending.id,
        outcome,
        notes: withNotes || undefined,
      });
      const message =
        outcome === "worked" ? "Thanks — playbook marked as worked." :
        outcome === "failed" ? "Got it — we'll factor that into your library scores." :
        outcome === "partial" ? "Recorded as partial." :
        "Skipped.";
      toast.success(message);
      setOpen(false);
      setPending(null);
      setShowNotes(false);
      setNotes("");
      setPendingOutcome(null);
    } catch (e) {
      toast.error("Couldn't save reflection", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const onPickOutcome = (outcome) => {
    if (outcome === "worked") {
      submit("worked");
      return;
    }
    setPendingOutcome(outcome);
    setShowNotes(true);
  };

  const platformLabel = (p) => ({
    cursor: "Cursor", lovable: "Lovable", bolt: "Bolt", v0: "v0",
    emergent: "Emergent", "ai-assistants": "your AI assistant",
    "vibe-code": "your vibe-code platform", "no-code": "your no-code builder",
    "product-planning": "your planning tool", general: "your AI builder",
  })[p] || "your AI builder";

  if (!pending) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) submit("skipped"); }}>
      <DialogContent
        data-testid="reflection-prompt"
        className="rounded-none border-[var(--pf-border)] sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold flex items-center justify-between">
            <span>How did it go?</span>
            <button
              onClick={() => submit("skipped")}
              disabled={submitting}
              data-testid="reflection-close"
              className="text-[var(--pf-text-secondary)] hover:text-[var(--pf-text)] -mt-1"
              aria-label="Skip reflection"
            >
              <X className="w-4 h-4" />
            </button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <p className="text-sm text-[var(--pf-text-secondary)] leading-relaxed">
            Last time you exported{" "}
            <strong className="text-[var(--pf-text)]">
              {pending.project_name || "your playbook"}
            </strong>{" "}
            to <strong className="text-[var(--pf-text)]">{platformLabel(pending.platform)}</strong>.
            Did it actually work?
          </p>

          {!showNotes && (
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={() => onPickOutcome("worked")}
                disabled={submitting}
                data-testid="reflection-worked"
                className="flex-col h-auto py-4 rounded-none bg-[var(--pf-bg)] border border-[var(--pf-border)] text-[var(--pf-text)] hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
              >
                <ThumbsUp className="w-5 h-5 mb-2" />
                <span className="text-xs font-medium">Worked</span>
              </Button>
              <Button
                onClick={() => onPickOutcome("partial")}
                disabled={submitting}
                data-testid="reflection-partial"
                className="flex-col h-auto py-4 rounded-none bg-[var(--pf-bg)] border border-[var(--pf-border)] text-[var(--pf-text)] hover:border-amber-500 hover:text-amber-600 hover:bg-amber-50"
              >
                <Minus className="w-5 h-5 mb-2" />
                <span className="text-xs font-medium">Partial</span>
              </Button>
              <Button
                onClick={() => onPickOutcome("failed")}
                disabled={submitting}
                data-testid="reflection-failed"
                className="flex-col h-auto py-4 rounded-none bg-[var(--pf-bg)] border border-[var(--pf-border)] text-[var(--pf-text)] hover:border-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <ThumbsDown className="w-5 h-5 mb-2" />
                <span className="text-xs font-medium">Didn't work</span>
              </Button>
            </div>
          )}

          {showNotes && pendingOutcome && (
            <div className="space-y-3">
              <label className="block text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)]">
                What happened? <span className="opacity-60">(optional)</span>
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  pendingOutcome === "failed"
                    ? "What broke? Which blocks didn't translate?"
                    : "What worked, what didn't?"
                }
                data-testid="reflection-notes"
                rows={3}
                maxLength={1000}
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none text-sm"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => { setShowNotes(false); setPendingOutcome(null); setNotes(""); }}
                  variant="ghost"
                  disabled={submitting}
                  className="rounded-none flex-1 h-9"
                >
                  Back
                </Button>
                <Button
                  onClick={() => submit(pendingOutcome, notes)}
                  disabled={submitting}
                  data-testid="reflection-submit-with-notes"
                  className="rounded-none flex-1 h-9 bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white font-medium"
                >
                  {submitting ? "Saving..." : "Submit"}
                </Button>
              </div>
            </div>
          )}

          {!showNotes && (
            <p className="text-[11px] text-[var(--pf-text-secondary)] text-center">
              Your answer updates quality scores on the library blocks you used.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
